export const HOCKEY_CHALLENGE_USE_CASE = 'HOCKEY_CHALLENGE';

export const SKILL_TESTING_OPERATIONS = ['START', 'ADD', 'SUBTRACT', 'MULTIPLY', 'DIVIDE'];

export const SKILL_TESTING_OUTCOMES = {
  NOT_STARTED: 'NOT_STARTED',
  IN_PROGRESS: 'IN_PROGRESS',
  PASSED: 'PASSED',
  LOCKED_OUT: 'LOCKED_OUT',
  BYPASSED: 'BYPASSED',
  NOT_REQUIRED: 'NOT_REQUIRED',
};

export const OPERATION_STRING_KEYS = {
  START: 'quiz/question/start/text',
  ADD: 'quiz/question/add/text',
  SUBTRACT: 'quiz/question/subtract/text',
  MULTIPLY: 'quiz/question/multiply/text',
  DIVIDE: 'quiz/question/divideBy/text',
};

const OPERATION_SET = new Set(SKILL_TESTING_OPERATIONS);
const OUTCOME_SET = new Set(Object.values(SKILL_TESTING_OUTCOMES));

function pickDisplay(display) {
  if (typeof display === 'string' && display.length > 0) {
    return { display };
  }
  return {};
}

export function toFiniteNumber(value) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
}

/**
 * Guest adapter for the shared STQ payload. Reads steps/choices only —
 * prompt, label, season, and any answer key are defects, not fields to bind.
 */
export function adaptSkillTestingQuestion(payload) {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  return {
    useCase: payload.useCase,
    outcome: payload.outcome,
    remainingAttempts: payload.remainingAttempts,
    steps: Array.isArray(payload.steps)
      ? payload.steps.map((step) => ({
          operation: step.operation,
          value: toFiniteNumber(step.value) ?? step.value,
          ...pickDisplay(step.display),
        }))
      : [],
    choices: Array.isArray(payload.choices)
      ? payload.choices.map((choice) => ({
          id: choice.id,
          value: toFiniteNumber(choice.value) ?? choice.value,
          ...pickDisplay(choice.display),
        }))
      : [],
  };
}

export function isValidPlatformQuestion(question) {
  if (!question || typeof question !== 'object') {
    return false;
  }
  if (!OUTCOME_SET.has(question.outcome)) {
    return false;
  }
  if (!Array.isArray(question.steps) || question.steps.length === 0) {
    return false;
  }
  if (!Array.isArray(question.choices) || question.choices.length === 0) {
    return false;
  }

  const stepsOk = question.steps.every(
    (step) => step && OPERATION_SET.has(step.operation) && toFiniteNumber(step.value) != null,
  );
  const choicesOk = question.choices.every(
    (choice) =>
      choice && choice.id != null && String(choice.id).trim().length > 0 && toFiniteNumber(choice.value) != null,
  );

  return stepsOk && choicesOk;
}

export function isFallbackError(error) {
  return error?.code === 'FEATURE_DISABLED' || error?.code === 'STQ_UNAVAILABLE';
}

export function shouldFallbackFromPlatformError(error) {
  return isFallbackError(error);
}

const TRANSPORT_CODES = new Set(['ECONNABORTED', 'ERR_CANCELED', 'ERR_NETWORK']);

/**
 * Gateway never answered with a usable GraphQL body (status 0, CORS, abort,
 * or a non-JSON parse). Distinct from FEATURE_DISABLED / STQ_UNAVAILABLE.
 */
export function isPlatformTransportFailure(error) {
  if (!error || typeof error !== 'object') {
    return false;
  }
  if (error.isNetworkError) {
    return true;
  }
  if (error.status === 0 || error.response?.status === 0) {
    return true;
  }
  if (TRANSPORT_CODES.has(error.code)) {
    return true;
  }
  if (error instanceof SyntaxError) {
    return true;
  }
  const message = typeof error.message === 'string' ? error.message : '';
  if (message === 'Network Error') {
    return true;
  }
  if (/canceled|aborted/i.test(message)) {
    return true;
  }
  if (/Unexpected token|JSON Parse|non-json/i.test(message)) {
    return true;
  }
  return false;
}

export function shouldShowQuestion(outcome) {
  return outcome === SKILL_TESTING_OUTCOMES.NOT_STARTED || outcome === SKILL_TESTING_OUTCOMES.IN_PROGRESS;
}

export function shouldShowLockout(outcome) {
  return outcome === SKILL_TESTING_OUTCOMES.LOCKED_OUT;
}

export function shouldAllowPlay(outcome) {
  return (
    outcome === SKILL_TESTING_OUTCOMES.PASSED ||
    outcome === SKILL_TESTING_OUTCOMES.BYPASSED ||
    outcome === SKILL_TESTING_OUTCOMES.NOT_REQUIRED
  );
}

export function mapPlatformSteps(steps, strings) {
  return (steps || [])
    .filter((step) => step && step.operation !== 'START')
    .map((step, index) => ({
      id: `question-${step.operation}-${index}`,
      operator: strings[OPERATION_STRING_KEYS[step.operation]] || '',
      operation:
        typeof step.display === 'string' && step.display.length > 0
          ? step.display
          : toFiniteNumber(step.value) ?? step.value,
    }));
}

export function mapPlatformChoices(choices) {
  return (choices || []).map((choice) => ({
    id: choice.id,
    title:
      typeof choice.display === 'string' && choice.display.length > 0
        ? choice.display
        : toFiniteNumber(choice.value) ?? choice.value,
  }));
}

export function buildQuizView({
  platformQuestion,
  questions,
  options,
  attempts,
  lockout,
  strings,
  useLegacyStq = true,
}) {
  if (lockout) {
    return {
      gameLocked: true,
      question: [],
      answerOptions: [],
      title: strings['notifications/quiz/gameLocked/checkBack/text'],
      text: strings['notifications/quiz/gameLocked/text'],
      lockedText: strings['notifications/quiz/gameLocked/checkBack/text'],
    };
  }

  if (isValidPlatformQuestion(platformQuestion)) {
    if (shouldAllowPlay(platformQuestion.outcome)) {
      return {
        gameLocked: false,
        question: [],
        answerOptions: [],
      };
    }
    return {
      gameLocked: false,
      question: mapPlatformSteps(platformQuestion.steps, strings),
      answerOptions: mapPlatformChoices(platformQuestion.choices),
    };
  }

  if (!useLegacyStq) {
    return {
      gameLocked: false,
      question: [],
      answerOptions: [],
    };
  }

  const currentQuestion = questions[attempts] || questions[0];
  const currentOptions = options[attempts] || options[0];

  return {
    gameLocked: false,
    question: [
      {
        id: `question-${attempts}-multiply`,
        operator: strings['quiz/question/multiply/text'],
        operation: currentQuestion[0],
      },
      {
        id: `question-${attempts}-divide`,
        operator: strings['quiz/question/divideBy/text'],
        operation: currentQuestion[1],
      },
      {
        id: `question-${attempts}-add`,
        operator: strings['quiz/question/add/text'],
        operation: currentQuestion[2],
      },
      {
        id: `question-${attempts}-subtract`,
        operator: strings['quiz/question/subtract/text'],
        operation: currentQuestion[3],
      },
    ],
    answerOptions: currentOptions.map((option) => ({
      id: option,
      title: option,
    })),
  };
}
